import {
  CallerIdentity as CallerIdentityDto,
  User as UserDto,
  Employee as EmployeeDto,
  PermissionEnum,
  SetUserLanguageRequest,
} from '@electricjs/core_entity-client';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import {
  CallerIdentityWithOrganizations,
  OrganizationUserRoles,
  User,
} from '@/types/users';
import { Division } from '@/types/divisions';
import { Employee } from '@/types/employees';
import { transformEmployee } from './employeeApiSlice';

// Division fields returned per org by /v1/users/me and /v1/users/me/identities
// (core-entity#4880) - not yet in @electricjs/core_entity-client.
type OrganizationDivisionsDto = {
  organization_has_divisions?: boolean;
  divisions?: Division[];
};

// GET /v1/users/me: CallerIdentityDto plus the division fields on each org.
export type CallerIdentityWithDivisionsDto = Omit<
  CallerIdentityDto,
  'organizations'
> & {
  organizations: Record<
    string,
    CallerIdentityDto['organizations'][string] & OrganizationDivisionsDto
  >;
};

// Manual type for GET /v1/users/me/identities - not yet in
// @electricjs/core_entity-client. Same per-entry/per-org shape as
// CallerIdentityDto, except each org also carries a `name` (this endpoint's
// org shape, unlike /v1/users/me's, includes it for display purposes).
type UserIdentityDto = {
  id: string;
  email: string;
  user_language: string | null;
  organizations: Record<
    string,
    {
      name: string;
      employee_id: string | null;
      roles: OrganizationUserRoles;
      permissions: PermissionEnum[];
    } & OrganizationDivisionsDto
  >;
};

export function transformUser(user: UserDto): User {
  return {
    id: user.id,
    email: user.email,
    auth0Id: user.auth0_id,
  };
}

export function transformCallerIdentityToUserWithOrganizations(
  CallerIdentityDto: CallerIdentityWithDivisionsDto
): CallerIdentityWithOrganizations {
  return {
    id: CallerIdentityDto.id,
    email: CallerIdentityDto.email,
    userLanguage: CallerIdentityDto.user_language,
    organizations: Object.keys(CallerIdentityDto.organizations).reduce(
      (acc, orgId) => {
        const { employee_id, ...orgData } =
          CallerIdentityDto.organizations[orgId];
        return {
          ...acc,
          [orgId]: {
            ...orgData,
            employeeId: employee_id,
          },
        };
      },
      {}
    ),
  };
}

export function transformUserIdentity(
  identity: UserIdentityDto
): CallerIdentityWithOrganizations {
  return {
    id: identity.id,
    email: identity.email,
    userLanguage: identity.user_language ?? undefined,
    organizations: Object.keys(identity.organizations).reduce((acc, orgId) => {
      const { employee_id, ...orgData } = identity.organizations[orgId];
      return {
        ...acc,
        [orgId]: {
          ...orgData,
          employeeId: employee_id ?? undefined,
        },
      };
    }, {}),
  };
}

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getMyUserData: builder.query<CallerIdentityWithOrganizations, void>({
      query: () => '/v1/users/me',
      forceRefetch: () => true,
      transformResponse: (CallerIdentityDto: CallerIdentityWithDivisionsDto) =>
        transformCallerIdentityToUserWithOrganizations(CallerIdentityDto),
      providesTags: [TagTypes.MyUserData],
    }),
    // /v1/users/me collapses to a single identity and 400s when the caller's
    // auth0_id maps to more than one distinct user record (e.g. multi-tenant
    // isolved users). This returns one entry per distinct user instead, with
    // no disambiguation and no 400 on multiplicity.
    getMyUserIdentities: builder.query<CallerIdentityWithOrganizations[], void>(
      {
        query: () => '/v1/users/me/identities',
        forceRefetch: () => true,
        transformResponse: (identities: UserIdentityDto[]) =>
          identities.map(transformUserIdentity),
        providesTags: [TagTypes.MyUserData],
      }
    ),
    getUserById: builder.query<User, string>({
      query: userId => `/v2/users/${userId}`,
      transformResponse: (user: UserDto) => transformUser(user),
      providesTags: (_result, _error, userId) => [
        { type: TagTypes.User, id: userId },
      ],
    }),
    getEmployeeByUserId: builder.query<
      Employee,
      { organizationId: string; userId: string }
    >({
      query: ({ organizationId, userId }) =>
        `/v1/organizations/${organizationId}/users/${userId}/employee`,
      transformResponse: (employee: EmployeeDto) => {
        return transformEmployee(employee);
      },
    }),
    resendVerificationEmail: builder.mutation<void, { email: string }>({
      query: ({ email }) => {
        return {
          url: `/v1/users/${email}/resend-verification-email`,
          method: 'POST',
        };
      },
    }),
    updateUserLanguage: builder.mutation<User, { language: string }>({
      query: ({ language }) => {
        const body: SetUserLanguageRequest = {
          // Backend expects lowercase language codes (e.g., "fr-ca" not "fr-CA")
          language: language.toLowerCase(),
        };

        return {
          url: '/v1/users/me/language',
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [
        TagTypes.MyUserData,
        TagTypes.Product, // product's name, description and properties are translated in the backend
        TagTypes.ProductSuggestions,
        TagTypes.Order, // contains product details (that are translated in the backend)
        TagTypes.HardwareQuote,
      ],
    }),
  }),
});

// export auto-generated hooks
export const {
  useGetMyUserDataQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useLazyGetMyUserDataQuery,
  useLazyGetMyUserIdentitiesQuery,
  useGetEmployeeByUserIdQuery,
  useResendVerificationEmailMutation,
  useUpdateUserLanguageMutation,
} = userApiSlice;
